import {
  Alert,
  Button,
  Flex,
  Heading,
  IconInfoSolid,
  IconPlusLine,
  ScreenReaderContent,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { createContext, useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import APToggle from "../components/Assessment/APToggle/APToggle";
import {
  ERROR,
  useNotification,
} from "../components/CustomHooks/NotificationProvider";
import { AssessmentPlanService } from "../services/AssessmentService";
import { AssignmentTypeService } from "../services/AssignmentTypeService";
import { CourseLevelService } from "../services/CourseLevelService";
import { CyclesService } from "../services/CyclesService";
import { ProgramService } from "../services/ProgramService";
import { SubjectService } from "../services/SubjectService";
import { MasteryLevelContext, UserDetailContext } from "../utils/Context";

export const ApDataContext = createContext();

export const Plan = () => {
  const userDetails = useContext(UserDetailContext);
  const { masteryLevels } = useContext(MasteryLevelContext);
  const showNotification = useNotification();
  const location = useLocation();
  const navigate = useNavigate();
  const [allPlans, setAllPlans] = useState([]);
  const [assessmentPlans, setAssessmentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [courseLevels, setCourseLevels] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [availCycles, setAvailCycles] = useState([]);
  const [masteryError, setMasteryError] = useState(false);

  const hasAccess =
    userDetails.role === "ac" ||
    (userDetails.role === "pc" && userDetails.ac_subjects.length > 0);

  useEffect(() => {
    if (!!location?.state?.plan && !loading && cycles.length > 0) {
      navigate("/plan/manage-plan", {
        state: {
          plan: location?.state?.plan,
          cycles: cycles,
          courseLevels: courseLevels,
          assignmentTypes: assignmentTypes,
          acSubs: subjects,
        },
      });
    }
  }, [location, loading, cycles, navigate]);

  useEffect(() => {
    if (masteryLevels) {
      const maps = masteryLevels;
      maps.forEach((map) => {
        if (map.is_canvas_default && !map.is_mapped) {
          setMasteryError(true);
        }
      });
    }
  }, [masteryLevels]);

  useEffect(() => {
    const fetchAssessmentPlans = async () => {
      try {
        const subs = await SubjectService.getSubjects({ allSubjects: true });
        setSubjects(subs);

        let plans = [];
        if (userDetails.isAC()) {
          // Fetch all plans if the user is an AC
          plans = await AssessmentPlanService.getLightAssessmentPlans();
          setAllPlans(plans);
          setAssessmentPlans(plans);
        } else {
          // Fetch assigned plans if user is a PC
          const assignedSubjects = subs
            .filter((subject) =>
              userDetails.ac_subjects.includes(subject.title)
            )
            .map((subject) => subject.id);

          const promiseList = [
            AssessmentPlanService.getLightAssessmentPlans(),
            AssessmentPlanService.getAssessmentPlansBySubjects(
              assignedSubjects
            ),
          ];

          const [allPlans, plansBySubject] = await Promise.all(promiseList);
          setAllPlans(allPlans);
          findRelevantPlans(allPlans, plansBySubject);
        }

        const promiseList = [
          CourseLevelService.getCourseLevels(),
          CyclesService.getCycles(),
          AssignmentTypeService.getAssignmentTypes(),
          ProgramService.getPrograms(),
        ];
        const [levels, cycles, types, programs] =
          await Promise.all(promiseList);
        setCourseLevels(levels);
        cycles.forEach((cycle) => {
          cycle.label = cycle.name;
        });
        setCycles(cycles);
        setAssignmentTypes(
          types.map((type) => ({
            id: type.id + "",
            title: type.title,
            name: type.name,
            label: type.name,
          }))
        );
        setPrograms(programs);
        setLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: "Assessment Plans" }),
          ERROR
        );
      }
    };

    if (userDetails.role) {
      if (hasAccess) {
        fetchAssessmentPlans();
      } else {
        setLoading(false);
      }
    }
  }, [userDetails, showNotification]);

  useEffect(() => {
    const availCycles = [];
    cycles.forEach((cycle) => {
      if (!allPlans.some((plan) => plan.cycle.id === cycle.id)) {
        availCycles.push(cycle);
      }
    });
    setAvailCycles(availCycles);
  }, [cycles, allPlans]);

  const reloadPlans = async () => {
    try {
      setLoading(true);
      if (userDetails.isAC()) {
        // Fetch all plans if the user is an AC
        const plans = await AssessmentPlanService.getLightAssessmentPlans();
        setAssessmentPlans(plans);
      } else {
        // Fetch assigned plans if user is a PC
        const assignedSubjects = subjects
          .filter((subject) => userDetails.ac_subjects.includes(subject.title))
          .map((subject) => subject.id);

        const promiseList = [
          AssessmentPlanService.getLightAssessmentPlans(),
          AssessmentPlanService.getAssessmentPlansBySubjects(assignedSubjects),
        ];

        const [allPlans, plansBySubject] = await Promise.all(promiseList);
        findRelevantPlans(allPlans, plansBySubject);
      }
    } catch {
      showNotification(
        t("notifications.general.fetchError", {
          obj: t("curriculumMap.planHeader"),
        }),
        ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const findRelevantPlans = (allPlans, plansBySubject) => {
    allPlans.forEach((plan) => {
      if (
        !plansBySubject.some((p) => p.id === plan.id) &&
        plan.results_purl && // Possible this could be null if no sections/measures in plan
        plan.results_purl.reviewers.includes(userDetails.email)
      ) {
        plansBySubject.push(plan);
      }
    });
    plansBySubject = plansBySubject.sort(
      (a, b) => new Date(b.date_created) - new Date(a.date_created)
    );
    setAssessmentPlans(plansBySubject);
  };

  const removePlanFromList = (planId) => {
    setAssessmentPlans(assessmentPlans.filter((plan) => plan.id !== planId));
    setAllPlans(allPlans.filter((plan) => plan.id !== planId));
  };

  return (
    <div>
      <Heading as="h1" level="h2" border="bottom" margin="0 0 small 0">
        {t("assessment.planAssessmentModalTitle")}
        <Tooltip
          renderTip={
            <div style={{ width: 600 }}>
              {t("assessment.planAssessmentDescription")}
            </div>
          }
          placement="end"
          on={["click", "hover", "focus"]}
          color="primary-inverse"
        >
          <button style={{ backgroundColor: "#fff", border: "none" }}>
            <IconInfoSolid color="brand" />
            <ScreenReaderContent>
              {t("assessment.planAssessmentDescription")}
            </ScreenReaderContent>
          </button>
        </Tooltip>
      </Heading>
      {hasAccess && (
        <Flex direction="row-reverse" wrap="wrap" gap="small">
          <Flex.Item>
            {loading ? (
              <Skeleton
                count={1}
                style={{ height: 38, width: 230, borderRadius: 5 }}
              />
            ) : (
              <Link
                to="manage-plan"
                state={{
                  cycles: cycles,
                  courseLevels: courseLevels,
                  assignmentTypes: assignmentTypes,
                  acSubs: subjects,
                  availCycles: availCycles,
                  programs: programs,
                }}
              >
                <Button
                  color="primary"
                  renderIcon={IconPlusLine}
                  key="assessment-plan-button"
                >
                  {t("assessment.addAssessmentPlan")}
                </Button>
              </Link>
            )}
          </Flex.Item>
          <Flex.Item>
            {loading ? (
              <Skeleton
                count={1}
                style={{ height: 38, width: 230, borderRadius: 5 }}
              />
            ) : (
              <Link
                to="create-plan-from-template"
                state={{
                  cycles: cycles,
                  courseLevels: courseLevels,
                  assignmentTypes: assignmentTypes,
                  acSubs: subjects,
                  availCycles: availCycles,
                  programs: programs,
                }}
              >
                <Button
                  color="primary"
                  renderIcon={IconPlusLine}
                  key="assessment-plan-button"
                >
                  {t("assessment.addFromTemplate")}
                </Button>
              </Link>
            )}
          </Flex.Item>
        </Flex>
      )}
      {loading && hasAccess && (
        <Skeleton
          count={3}
          style={{ height: 64, borderRadius: 5, marginTop: 15 }}
        />
      )}
      {!loading && assessmentPlans.length === 0 && hasAccess && (
        <Alert variant="warning">
          {t("assessment.pleaseAddAssessmentPlan")}
        </Alert>
      )}
      {!loading && masteryError && hasAccess && (
        <Alert variant="warning">
          <Text>{t("masteryLevels.masteryAssessmentWarning")}</Text>
          <Link to="/setup/mastery">{t("masteryLevels.mapThemHere")}</Link>
        </Alert>
      )}
      {!loading && !hasAccess && (
        <Alert variant="info">
          <Text>{t("dashboard.noAssociatedSubjects")}</Text>
        </Alert>
      )}
      {!loading &&
        assessmentPlans.map((plan) => (
          <div style={{ marginTop: 15 }} key={plan.id}>
            <APToggle
              plan={plan}
              allCycles={cycles}
              courseLevels={courseLevels}
              subjects={subjects}
              reloadPlans={reloadPlans}
              assignmentTypes={assignmentTypes}
              programs={programs}
              removePlanFromList={removePlanFromList}
            />
          </div>
        ))}
      <Outlet />
    </div>
  );
};

export default Plan;
